/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from 'react'
import '../../../camera/CameraPerformance.css'
import {BottomButtonsWrapper} from '../../../camera/SensorCamera/ItemDetails/DetectionCapability/styled';
import {useMsal} from '@azure/msal-react';
import ArgosButton from "../../../../components/Buttons/ArgosButton";
import {useAppSelector} from "../../../../../redux/hooks";
import ArgosTextField from "../../../../components/Form/TextField/ArgosTextField";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {selectGlobalDialogState} from '../../../../../redux/ducks/GlobalDialog/slice';
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";

import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import {usePostRequest} from "../../../../../api/helper";
import RightPanel from './CollapsibleTabs/RightPanel';
import ObjectProperties from './CollapsibleTabs/ObjectProperties';
import LidarProperties from './CollapsibleTabs/LidarProperties';
import ArgosCollapseComponent from '../../../../components/CollapseComponent/ArgosCollapseComponent';
import {theme} from '../../../../../constants/theme';
import {getLidarPlotEndpoint} from '../../constants';
import { toast } from 'react-hot-toast';
import {set} from "lodash";
import VAR_COMMON_CONSTANTS from "../../../../../constants/globalVariables";
import { COLUMN_NAMES } from '../constants';


const Content = (props: any) => {

  const buildPlotData = (_data: any) => {
    const data: any = {x: _data?.x, y: _data?.y}
    const columns = {xLabel: COLUMN_NAMES.AZIMUTH_ANGLE, yLabel: COLUMN_NAMES.DISTANCE}
    const returnObj = {data, columns}
    return returnObj
  }

  const hasWriteAccess = useHasWriteAccess()
  const [baselineData, setBaselineData] = useState(props.baselineData)
  const [plotData, setPlotData] = useState(buildPlotData(props.sensorLidarData.plotData[Object.keys(props.sensorLidarData.plotData)[0]]?.data))
  const [fovAndBoresightLidarValues, setFovAndBoresightLidarValues] = useState<any>({});
  const [objectData, setObjectData] = useState(props.objectData);
  const [objectDataMap, setObjectDataMap] = useState(props.objectDataMap);
  const [configName, setConfigName] = useState("");
  const [openSavePopup, setOpenSavePopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [isLoadingPlot, setIsLoadingPlot] = useState(false);
  const gmid = props.gmid
  const isDialogOpen = useAppSelector(selectGlobalDialogState).isOpen
  const [isError, setIsError] = useState(false);
  const [componentDataMap, setComponentDataMap] = useState<any>({});
  const [temperatureArray, setTemperatureArray] = useState<number[]>([]);
  const [sensorLidarData, setSensorLidarData] = useState<any>(props?.sensorLidarData)
  useEffect(() => {
    setConfigName(baselineData['configName'])
  }, [baselineData?.['configName']]);

  useEffect(() => {
    setBaselineData(props.baselineData);
    setObjectData(props.objectData);
    setObjectDataMap(props.objectDataMap);
    const objectName = props.baselineData?.selectedObject
    let _plotData = props.sensorLidarData?.plotData[getPlotDataName(props.baselineData.temperature)]?.data
    setPlotData(buildPlotData(_plotData))
    getTemperatureArray();
  }, [props])

  useEffect(() => {
    handleUpdatePlot(props.baselineData)
  }, [props.configIndex])


  const [newTempRelatedDataList, setTempRelatedDataList] = useState<any[]>( props?.tempToObjMap[23]);
  useEffect(() => {
    setTempRelatedDataList(props?.tempToObjMap[23] ?? {})
  }, [props.tempToObjMap]);
  function handleTempChange(newTempValue: string ) {
    const _newTempRelatedDataList: any =  props?.tempToObjMap[newTempValue as unknown as number]
    const newObject = {
      ...baselineData,
      "tempRelatedDataList": _newTempRelatedDataList,
      'temperature': newTempValue,
      "selectedObject" : _newTempRelatedDataList?.objectName,
      "selectedPointsFrames" : _newTempRelatedDataList?.pointsFrames
    }
    setSensorLidarData({
      ...sensorLidarData,
    })
    setTempRelatedDataList(_newTempRelatedDataList)
    setBaselineData(newObject)
  }

  const getTemperatureArray = () => {
    const sensorData = props.sensorLidarData.plotData
    let _temperatureArray: number[] = [];
    Object.keys(sensorData)?.forEach((key:string) => {
      if(key != "instrumentationLimits"){
        const detectiondata = sensorData[key].data
        const tempValue = Object.keys(detectiondata).filter((keys: string) => keys.includes("temperature") &&  keys.includes("value"))[0] as unknown as number
        if (!_temperatureArray.includes(detectiondata[tempValue])){
          _temperatureArray.push(detectiondata[tempValue])
        }

      }
    })
    setTemperatureArray(_temperatureArray)
  }
  const getObjectIdBasedName = () => {
    const objectId = objectData.filter((objectElement: any) => objectElement.name === baselineData?.selectedObject)[0]?.id
    return objectId
  }
  const url = getLidarPlotEndpoint
  const getLidarPlotHandle = usePostRequest(url)
  async function handleUpdatePlot(baselineData: any) {
    setIsLoadingPlot(true)
    // if(baselineData?.selectedObject == "" || baselineData?.selectedObject == undefined){
    //   toast.error("Please select an Object to update plot")
    //   setIsLoadingPlot(false)
    //   return
    // }
    let plotDataKey: string = getPlotDataName(baselineData.temperature)

    let newPlotData = {
      'objectId': getObjectIdBasedName(),
      'instrumentationRange' : baselineData["maxInstrumentedRange"],
      'instrumentationAngle':  baselineData["azimuthFOV"],
      "plotData": props.sensorLidarData.plotData[plotDataKey]?.data,
    }

    getLidarPlotHandle(newPlotData).then((responseJson) => {
      setIsError(false)
      const fovAndBoresightLidarObject = {
        'azimuth_fov': responseJson["azimuth_fov"],
        'elevation_fov': responseJson["elevation_fov"],
        'max_range': responseJson["max_range"]
      }
      const data = {
        'x': responseJson['x'],
        'y': responseJson['y']
      }
      setPlotData(buildPlotData(data))
      setFovAndBoresightLidarValues(fovAndBoresightLidarObject)
      setIsLoadingPlot(false);
    }).catch(err => {
      toast.error("Error while updating plot. Please check your values and try again")
      setIsError(true)
      setIsLoadingPlot(false)
    })
  }

  function handleReset() {
    setBaselineData(props.baselineData)
  }

  function getPlotDataName(_tempValue: string) {
    const sensorData = props.sensorLidarData.plotData
    let detectionDataKey: any = ""
    Object.keys(sensorData)?.forEach((key:string) => {
      if(key != "instrumentationLimits"){
        const detectiondata = sensorData[key].data
        if( (key === 'Detection_Range_1' || key === 'Detection_Range_2') && props.configIndex != 0){
          return
        }
        const tempValue = Object.keys(detectiondata).filter((keys: string) => keys.includes("temperature") &&  keys.includes("value"))[0]
        if(detectiondata[tempValue] == _tempValue){
          detectionDataKey = key
        }
      }
      
    })
    return detectionDataKey
  }

  function onSaveConfiguration() {
    props.onSaveConfiguration(null, baselineData, configName, gmid)
  }

  function onOverrideConfiguration() {
    props.onSaveConfiguration(props.configIndex - 1, baselineData, configName, gmid)
  }

  const handleClose = () => {
    setOpenSavePopup(false);
    setOpenDeletePopup(false);
  };

  const handleClickSaveOpen = () => {
    setOpenSavePopup(true);
  };

  function handleSaveConfigName(e: any) {
    setConfigName(e.target.value)
  }

  function onDeleteConfiguration() {
    props.onDeleteConfiguration(props.baselineData.configName, props.baselineData.owner)
  }


  function setIsPublicConfig(_isPublicConfig: any) {
    const newObject = {
      ...baselineData,
      'isPublicConfig': _isPublicConfig
    }
    setBaselineData(newObject);
  }

  useEffect(() => {
    createCollapsibleTabs()
  }, [props, baselineData, objectDataMap, plotData, openSavePopup, configName, newTempRelatedDataList])

  const setSelectedObject = (newObjectValue: string) => {
    setBaselineData({
        ...baselineData,
        "selectedObject": newObjectValue
      }
    )
  }

  const setSelectedPointsFrames = (newPointsFrames: string) => {
    setBaselineData({
        ...baselineData,
        "selectedPointsFrames": newPointsFrames
      }
    )
  }

  const createCollapsibleTabs = () => {
    let _componentDataMap: any = {}
    _componentDataMap["Lidar Properties"] = <LidarProperties
      temperatureArray={temperatureArray}
      sensorLidarData={sensorLidarData}
      isDialogOpen={isDialogOpen}
      handleTempChange={handleTempChange}
      baselineData={baselineData}
    />
    _componentDataMap[VAR_COMMON_CONSTANTS.sensorApp.all.SENSOR_TESTING_CONDITIONS_HEADER] = <ObjectProperties
      baselineData={baselineData}
      isDialogOpen={isDialogOpen}
      gmid={gmid}
      configIndex={gmid}
      newDataList={newTempRelatedDataList}
      setIsPublicConfig={setIsPublicConfig}
      setSelectedObject={setSelectedObject}
      setSelectedPointsFrames={setSelectedPointsFrames}
      temperatureArray={temperatureArray}
      handleTempChange={handleTempChange}
    />
    _componentDataMap["Sensor Performance"] = <RightPanel 
      isLoadingPlot={isLoadingPlot}
      isError={isError}
      plotData={plotData}
      handleUpdatePlot={handleUpdatePlot}
      baselineData={baselineData}
      handleReset={handleReset}
      handleClickSaveOpen={handleClickSaveOpen}
      openSavePopup={openSavePopup}
      handleClose={handleClose}
      handleSaveConfigName={handleSaveConfigName}
      configName={configName}
      configIndex={props?.configIndex}
      gmid={gmid}
      onOverrideConfiguration={onOverrideConfiguration}
      onSaveConfiguration={onSaveConfiguration}
      onDeleteConfiguration={onDeleteConfiguration}
      fovAndBoresightLidarValues={fovAndBoresightLidarValues}
    />
    setComponentDataMap(_componentDataMap)
  }

  return (
    <ArgosFlexbox vertical style={{overflow: 'auto', height: '100%', width: '100%'}}>
      {/* Make it own component */}
      <ArgosFlexbox style={{alignContent: 'center'}}>
        <BottomButtonsWrapper style={{marginTop: '0px'}}>
          <ArgosButton disabled={isLoadingPlot ||
            baselineData?.selectedObject === "" ||
            baselineData?.selectedObject === undefined}
                       onClick={() => handleUpdatePlot(baselineData)}>Update Plot</ArgosButton>
        </BottomButtonsWrapper>
      </ArgosFlexbox>
     <div style={{ flex: 1, overflow: 'auto' }}>
      <ArgosCollapseComponent
        styleProps={{
          outlineOnly: true,
          fontColor: theme.layout.sidebar.backgroundColor,
        }}
        defaultOpen={true}
        data={componentDataMap ?? {}}
      />
    </div>
        
    </ArgosFlexbox>
  )
}
export default Content